import * as path from 'node:path';
import { TadaError } from './errors';
import { getURLConfig } from './loaders';
import type { SchemaOrigin } from './loaders';

export interface BaseConfig {
  template?: string;
  trackFieldUsage?: boolean;
  shouldCheckForColocatedFragments?: boolean;
}

export interface SchemaConfig {
  name?: string;
  schema: SchemaOrigin;
  tadaOutputLocation?: string;
  tadaTurboLocation?: string;
  tadaPersistedLocation?: string;
}

const SCHEMA_PROPS = [
  'name',
  'tadaOutputLocation',
  'tadaTurboLocation',
  'tadaPersistedLocation',
] as const;

interface MultiSchemaConfig extends SchemaConfig {
  name: string;
}

export type GraphQLSPConfig = BaseConfig & (SchemaConfig | { schemas: MultiSchemaConfig[] });

const parseSchemaConfig = (input: unknown, rootPath: string): SchemaConfig => {
  const resolveConfigDir = (input: string | undefined) => {
    if (!input) return input;
    return path.normalize(
      input.replace(/\${([^}]+)}/, (_match, name) => {
        if (name === 'configDir') {
          return rootPath;
        } else {
          throw new TadaError(
            `Substitution "\${${name}}" is not recognized (did you mean 'configDir'?)`
          );
        }
      })
    );
  };

  if (input == null || typeof input !== 'object') {
    throw new TadaError(`Schema is not configured properly (Received: ${input})`);
  }

  if ('schema' in input && input.schema && typeof input.schema === 'object') {
    const { schema } = input;
    if (!('url' in schema)) {
      throw new TadaError('Configuration contains a `schema` object, but no `url` property');
    }

    if ('headers' in schema && schema.headers && typeof schema.headers === 'object') {
      for (const key in schema.headers) {
        if (schema.headers[key] && typeof schema.headers[key] !== 'string') {
          throw new TadaError(
            'Headers at `schema.headers` contain a non-string value at key: ' + key
          );
        }
      }
    } else if ('headers' in schema) {
      throw new TadaError(
        "Configuration contains a `schema.headers` property, but it's not an object"
      );
    }
  } else if (!('schema' in input) || typeof input.schema !== 'string') {
    throw new TadaError('Configuration is missing a `schema` property');
  }

  if (
    'tadaOutputLocation' in input &&
    input.tadaOutputLocation &&
    typeof input.tadaOutputLocation !== 'string'
  ) {
    throw new TadaError(
      "Configuration contains a `tadaOutputLocation` property, but it's not a file path"
    );
  }

  if (
    'tadaTurboLocation' in input &&
    input.tadaTurboLocation &&
    typeof input.tadaTurboLocation !== 'string'
  ) {
    throw new TadaError(
      "Configuration contains a `tadaTurboLocation` property, but it's not a file path"
    );
  }

  if (
    'tadaPersistedLocation' in input &&
    input.tadaPersistedLocation &&
    typeof input.tadaPersistedLocation !== 'string'
  ) {
    throw new TadaError(
      "Configuration contains a `tadaPersistedLocation` property, but it's not a file path"
    );
  }

  const output = input as any as SchemaConfig;

  let schema: SchemaOrigin = output.schema;
  if (typeof schema === 'string') {
    const url = getURLConfig(schema);
    if (!url) schema = resolveConfigDir(schema) || schema;
  }

  return {
    ...output,
    schema,
    tadaOutputLocation: resolveConfigDir(output.tadaOutputLocation),
    tadaTurboLocation: resolveConfigDir(output.tadaTurboLocation),
    tadaPersistedLocation: resolveConfigDir(output.tadaPersistedLocation),
  };
};

export const parseConfig = (
  input: unknown,
  /** Defines the path of the "main" `tsconfig.json` file.
   * @remarks
   * This should be the `rootPath` output from `loadConfig`,
   * which is the path of the user's `tsconfig.json` before
   * resolving `extends` options.
   */
  rootPath: string = process.cwd()
): GraphQLSPConfig => {
  if (input == null || typeof input !== 'object') {
    throw new TadaError(`Configuration is of an invalid type (Received: ${input})`);
  } else if ('template' in input && input.template && typeof input.template !== 'string') {
    throw new TadaError("Configuration contains a `template` property, but it's not a string");
  } else if ('name' in input && input.name && typeof input.name !== 'string') {
    throw new TadaError("Configuration contains a `name` property, but it's not a string");
  }

  if ('schemas' in input) {
    if (!Array.isArray(input.schemas)) {
      throw new TadaError("Configuration contains a `schema` property, but it's not an array");
    }

    if ('schema' in input) {
      throw new TadaError(
        'If configuration contains a `schemas` property, it cannot contain a `schema` configuration.'
      );
    } else if ('tadaOutputLocation' in input) {
      throw new TadaError(
        "If configuration contains a `schemas` property, it cannot contain a 'tadaOutputLocation` configuration."
      );
    } else if ('tadaTurboLocation' in input) {
      throw new TadaError(
        "If configuration contains a `schemas` property, it cannot contain a 'tadaTurboLocation` configuration."
      );
    } else if ('tadaPersistedLocation' in input) {
      throw new TadaError(
        "If configuration contains a `schemas` property, it cannot contain a 'tadaPersistedLocation` configuration."
      );
    }

    const schemas = input.schemas.map((schema): MultiSchemaConfig => {
      if (!('name' in schema) || !schema.name || typeof schema.name !== 'string')
        throw new TadaError('All `schemas` configurations must contain a `name` label.');
      if (
        !('tadaOutputLocation' in schema) ||
        !schema.tadaOutputLocation ||
        typeof schema.tadaOutputLocation !== 'string'
      )
        throw new TadaError(
          'All `schemas` configurations must contain a `tadaOutputLocation` path.'
        );
      return {
        ...parseSchemaConfig(schema, rootPath),
        name: schema.name,
      };
    });

    for (const prop of SCHEMA_PROPS) {
      const values = schemas.map((schema) => schema[prop]).filter(Boolean);
      const uniqueValues = new Set(values);
      if (values.length !== uniqueValues.size)
        throw new TadaError(`All '${prop}' values in \`schemas[]\` must be unique.`);
    }

    return { ...input, schemas };
  } else {
    return { ...input, ...parseSchemaConfig(input, rootPath) };
  }
};

export interface ProjectConfig {
  /** Directory that relative output locations resolve against. */
  projectPath: string;
  config: GraphQLSPConfig;
  /** Display name for error messages, e.g. a relative tsconfig path. */
  label?: string;
}

const LOCATION_PROPS = [
  'tadaOutputLocation',
  'tadaTurboLocation',
  'tadaPersistedLocation',
] as const;

// Generated purely from the schema, so projects emitting it to the same path
// with the same schema produce identical contents. Turbo and persisted locations
// derive from each project's own documents instead, so a shared path clobbers.
const SCHEMA_DERIVED_PROPS = new Set<(typeof LOCATION_PROPS)[number]>(['tadaOutputLocation']);

const schemaIdentity = (projectPath: string, origin: SchemaOrigin | undefined): string => {
  if (!origin) return '';
  if (typeof origin === 'object')
    return `url:${origin.url}\0${JSON.stringify(origin.headers || null)}`;
  if (getURLConfig(origin)) return `url:${origin}`;
  let resolved = path.resolve(projectPath, origin);
  if (process.platform === 'win32') resolved = resolved.toLowerCase();
  return `file:${resolved}`;
};

export const validateUniqueOutputLocations = (projects: readonly ProjectConfig[]): void => {
  for (const prop of LOCATION_PROPS) {
    const seen = new Map<string, { label: string; schema: string }>();
    for (const project of projects) {
      const label = project.label || project.projectPath;
      const schemas = 'schemas' in project.config ? project.config.schemas : [project.config];
      for (const schema of schemas) {
        const location = schema[prop];
        if (!location) continue;
        let resolved = path.resolve(project.projectPath, location);
        if (process.platform === 'win32') resolved = resolved.toLowerCase();
        const identity = schemaIdentity(project.projectPath, schema.schema);
        const previous = seen.get(resolved);
        if (previous && previous.label !== label) {
          // Identical schema-derived output is a deliberately shared file, not a conflict.
          const isSharedOutput = SCHEMA_DERIVED_PROPS.has(prop) && previous.schema === identity;
          if (!isSharedOutput) {
            throw new TadaError(
              `Projects '${previous.label}' and '${label}' resolve '${prop}' to the same file: ${location}`
            );
          }
        } else if (!previous) {
          seen.set(resolved, { label, schema: identity });
        }
      }
    }
  }
};

export const getSchemaNamesFromConfig = (config: GraphQLSPConfig): Set<null | string> => {
  return new Set<null | string>([
    ...('schema' in config ? [null] : []),
    ...('schemas' in config ? config.schemas.map((input) => input.name) : []),
  ]);
};

export const getSchemaConfigForName = (
  config: GraphQLSPConfig,
  name: string | undefined
): SchemaConfig | null => {
  if (name && 'name' in config && config.name === name) {
    return config;
  } else if (!name && !('schemas' in config)) {
    return config;
  } else if (name && 'schemas' in config) {
    for (let index = 0; index < config.schemas.length; index++)
      if (config.schemas[index].name === name) return config.schemas[index];
    return null;
  } else {
    return null;
  }
};
